/* Product quantity offers. Prices are integer cents; no Shopify discount codes.
 * This is a client-side planner, NOT a server-side minimum-quantity control.
 * GOS must recognize every selected reference and enforce purchase conditions.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.GosQuantityOffers = api;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';
  const POINTS = [999, 1499, 1999, 2499, 2999, 3499, 3999, 4499];
  const MAX_UNITS = 100;

  function fail(code, message) {
    const error = new Error(message);
    error.code = code;
    throw error;
  }
  function variantId(value) {
    const id = String(value);
    if (!/^[1-9][0-9]*$/.test(id) || !Number.isSafeInteger(Number(id))) {
      fail('INVALID_VARIANT', 'La référence produit est invalide.');
    }
    return id;
  }
  function quantity(value) {
    if (!Number.isSafeInteger(value) || value < 1 || value > MAX_UNITS) {
      fail('INVALID_QUANTITY', 'La quantité doit être un entier entre 1 et 100.');
    }
    return value;
  }
  function compile(config) {
    if (!config || config.currency !== 'EUR' || !Array.isArray(config.groups)) {
      fail('INVALID_CONFIG', 'La configuration des offres est invalide.');
    }
    const variants = new Map();
    const groupKeys = new Set();
    const productFormats = new Set();
    const references = new Map();
    for (const group of config.groups) {
      const formatKey = JSON.stringify([group.productId, group.format]);
      if (!group.id || !group.productId || !group.format || groupKeys.has(group.id) || productFormats.has(formatKey)) {
        fail('INVALID_GROUP', 'Un produit et son format doivent avoir un seul groupe tarifaire.');
      }
      groupKeys.add(group.id);
      productFormats.add(formatKey);
      if (!Array.isArray(group.tiers) || !group.tiers.length || group.tiers[0].minimum !== 1) {
        fail('INVALID_TIERS', 'Le tarif à l’unité est obligatoire.');
      }
      let previousMinimum = 0;
      let previousPrice = Infinity;
      for (const tier of group.tiers) {
        quantity(tier.minimum);
        if (tier.minimum <= previousMinimum || !POINTS.includes(tier.unitPrice) || tier.unitPrice > previousPrice) {
          fail('INVALID_TIERS', 'Les paliers doivent être croissants en quantité et décroissants en prix.');
        }
        previousMinimum = tier.minimum;
        previousPrice = tier.unitPrice;
      }
      if (!Array.isArray(group.variants) || !group.variants.length) {
        fail('INVALID_GROUP', 'Une offre doit contenir au moins une variante.');
      }
      for (const variant of group.variants) {
        const id = variantId(variant.id);
        if (variants.has(id)) fail('DUPLICATE_VARIANT', 'Une variante ne peut appartenir à deux offres.');
        variants.set(id, { group, variant });
        for (const tier of group.tiers) {
          const ref = variant.references?.[tier.minimum];
          if (!ref) continue; // Missing references may be quoted, never handed to checkout.
          const target = variantId(ref.variantId);
          if (ref.unitPrice !== tier.unitPrice) fail('REFERENCE_PRICE_MISMATCH', 'Le prix catalogue ne correspond pas au tarif du lot.');
          if (references.has(target) && references.get(target).sourceId !== id) {
            fail('REFERENCE_REUSED', 'Une référence tarifaire ne peut représenter deux saveurs ou produits.');
          }
          if (references.has(target) && references.get(target).unitPrice !== ref.unitPrice) {
            fail('REFERENCE_PRICE_MISMATCH', 'Une référence catalogue ne peut avoir plusieurs prix.');
          }
          references.set(target, { sourceId: id, unitPrice: ref.unitPrice });
        }
      }
    }
    return variants;
  }

  function quote(config, cartLines) {
    const catalog = compile(config);
    if (!Array.isArray(cartLines)) fail('INVALID_CART', 'Le panier est invalide.');
    const byVariant = new Map();
    const groupQuantities = new Map();
    let totalQuantity = 0;
    for (const line of cartLines) {
      const id = variantId(line.variantId);
      const count = quantity(line.quantity);
      const entry = catalog.get(id);
      if (!entry) fail('UNKNOWN_VARIANT', 'Ce produit ne figure pas dans la configuration des offres.');
      totalQuantity += count;
      if (totalQuantity > MAX_UNITS) fail('INVALID_QUANTITY', 'Le panier dépasse 100 articles.');
      byVariant.set(id, (byVariant.get(id) || 0) + count);
      groupQuantities.set(entry.group.id, (groupQuantities.get(entry.group.id) || 0) + count);
    }
    const lines = [];
    for (const [id, count] of byVariant) {
      const { group, variant } = catalog.get(id);
      const groupQuantity = groupQuantities.get(group.id);
      const tier = group.tiers.filter(t => t.minimum <= groupQuantity).at(-1);
      const reference = variant.references?.[tier.minimum];
      lines.push({
        sourceVariantId: id, groupId: group.id, title: group.title, variantTitle: variant.title,
        quantity: count, groupQuantity, minimum: tier.minimum,
        unitPrice: tier.unitPrice, linePrice: tier.unitPrice * count,
        regularUnitPrice: group.tiers[0].unitPrice,
        referenceVerified: Boolean(reference?.verified === true && reference.unitPrice === tier.unitPrice),
        minimumQuantityVerified: tier.minimum === 1 || group.quantityRulesVerifiedByGos === true,
        checkoutVariantId: reference ? variantId(reference.variantId) : null,
      });
    }
    const subtotal = lines.reduce((sum, line) => sum + line.linePrice, 0);
    const regularSubtotal = lines.reduce((sum, line) => sum + line.regularUnitPrice * line.quantity, 0);
    return {
      currency: config.currency, totalQuantity, subtotal, regularSubtotal,
      savings: regularSubtotal - subtotal, lines,
      checkoutReady: lines.length > 0 && lines.every(line => line.referenceVerified && line.minimumQuantityVerified),
    };
  }

  function prepareHandoff(config, cartLines) {
    // Recompute from canonical lines: never accept a caller-supplied total.
    const result = quote(config, cartLines);
    if (!result.lines.length) fail('EMPTY_CART', 'Ajoutez un produit au panier.');
    if (result.lines.some(line => !line.referenceVerified)) {
      fail('GOS_REFERENCE_UNVERIFIED', 'Le tarif du lot doit être synchronisé et vérifié dans GOS avant de commander.');
    }
    if (result.lines.some(line => !line.minimumQuantityVerified)) {
      fail('GOS_QUANTITY_UNVERIFIED', 'Les conditions de quantité doivent être vérifiées côté GOS avant d’activer cette offre.');
    }
    if (config.checkoutOrigin !== 'https://checkout.inshape-pay.com') {
      fail('INVALID_CHECKOUT', 'La destination de paiement n’est pas celle de cette boutique.');
    }
    const grouped = new Map();
    for (const line of result.lines) {
      const previous = grouped.get(line.checkoutVariantId);
      if (previous && previous.unitPrice !== line.unitPrice) fail('REFERENCE_PRICE_MISMATCH', 'Une référence a plusieurs prix.');
      grouped.set(line.checkoutVariantId, {
        id: Number(line.checkoutVariantId), quantity: (previous?.quantity || 0) + line.quantity,
        unitPrice: line.unitPrice,
      });
    }
    const items = [...grouped.values()].map(({ id, quantity }) => ({ id, quantity }));
    const url = new URL(config.checkoutOrigin);
    url.searchParams.set('products', items.map(item => `${item.id}:${item.quantity}`).join(','));
    return { ...result, items, checkoutUrl: url.href };
  }

  function quotePack(config, choiceId, count = 1) {
    quantity(count);
    const pack = config.packOffers?.[String(choiceId)];
    if (!pack || !Array.isArray(pack.components) || pack.components.length < 2) fail('UNKNOWN_PACK', 'La composition du pack est inconnue.');
    const catalog = compile(config);
    const lines = pack.components.map(component => {
      const source = variantId(component.sourceVariantId), entry = catalog.get(source);
      if (!entry) fail('UNKNOWN_VARIANT', 'Un composant du pack est inconnu.');
      const n = quantity(component.quantity * count);
      const ref = Object.values(entry.variant.references).find(r => String(r.variantId) === String(component.checkoutVariantId));
      if (!ref || ref.unitPrice !== component.unitPrice || !POINTS.includes(ref.unitPrice)) fail('REFERENCE_PRICE_MISMATCH', 'Le tarif du pack ne correspond pas au catalogue.');
      return {sourceVariantId:source,groupId:entry.group.id,title:entry.group.title,variantTitle:entry.variant.title,
        quantity:n,unitPrice:ref.unitPrice,linePrice:ref.unitPrice*n,regularUnitPrice:entry.group.tiers[0].unitPrice,
        checkoutVariantId:variantId(ref.variantId),referenceVerified:ref.verified===true,
        minimumQuantityVerified:pack.conditionsVerifiedByGos===true,packChoiceId:String(choiceId)};
    });
    const totalQuantity = lines.reduce((n,l)=>n+l.quantity,0);
    if(totalQuantity>MAX_UNITS)fail('INVALID_QUANTITY','Le panier dépasse 100 articles.');
    const subtotal=lines.reduce((n,l)=>n+l.linePrice,0),regularSubtotal=lines.reduce((n,l)=>n+l.regularUnitPrice*l.quantity,0);
    return {currency:config.currency,totalQuantity,subtotal,regularSubtotal,savings:regularSubtotal-subtotal,lines,
      checkoutReady:lines.every(l=>l.referenceVerified&&l.minimumQuantityVerified)};
  }
  return Object.freeze({ POINTS: Object.freeze(POINTS), quote, quotePack, prepareHandoff });
});
